var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { readinessCheckLines } from "./readinessLog";
import { logDetails, outcomeChange, outcomeExplanation } from "./logMessage";
/** A check or next-step value: an i18n key with its parameters. */
var valueText = function (t, value) { var _a; return (value === null || value === void 0 ? void 0 : value.message_key) ? String(t(value.message_key, (_a = value.params) !== null && _a !== void 0 ? _a : {})) : ""; };
var outcomeText = function (t, explanation) {
    return String(t("logsScreen.scheduledOutcome.outcome.".concat(explanation.outcome)));
};
/** The reasons of an explanation: the deciding check, the authorization and the next step. */
var reasons = function (t, explanation) {
    var _a;
    var deciding = explanation.checks.find(function (check) { return check.id === explanation.deciding; });
    var lines = [
        String(t("logsScreen.scheduledOutcome.deciding", {
            check: t("logsScreen.scheduledOutcome.check.".concat(explanation.deciding)),
            value: valueText(t, deciding === null || deciding === void 0 ? void 0 : deciding.current),
        })),
    ];
    if ((_a = explanation.authorized_by) === null || _a === void 0 ? void 0 : _a.code) {
        lines.push(String(t("logsScreen.scheduledOutcome.authorizedBy", {
            code: explanation.authorized_by.code,
        })));
    }
    var nextStep = valueText(t, explanation.next_step);
    if (nextStep) {
        lines.push(String(t("logsScreen.scheduledOutcome.nextStep", { step: nextStep })));
    }
    return lines;
};
/** The Seal at close reasons a scheduled outcome records in `details.reason`. */
var SEAL_REASONS = ["ballot-box-seal-policy", "never-opened-kept-open"];
/** Why a scheduled outcome left a Post as it was (VOTE-FREEZE), in the admin's language. */
var sealReason = function (t, details) {
    var reason = details === null || details === void 0 ? void 0 : details.reason;
    return typeof reason === "string" && SEAL_REASONS.includes(reason)
        ? String(t("logsScreen.scheduledOutcome.reason.".concat(reason)))
        : null;
};
/** The values of a statement body variant, e.g. `{"BallotBoxSealed": [...]}`. */
var bodyValues = function (message, kind) {
    var _a;
    var body = (_a = message === null || message === void 0 ? void 0 : message.statement) === null || _a === void 0 ? void 0 : _a.body;
    if (!body || typeof body !== "object")
        return null;
    var values = body[kind];
    return Array.isArray(values) ? values : null;
};
var count = function (value) { return (typeof value === "number" ? value : Number(value) || 0); };
/**
 * Readable lines for the ballot box seal entries (VOTE-FREEZE): the seal
 * hash and the counts the signed statement carries, why the others don't
 * count, the Close voting request,
 * the reason a seal failed, and what a tally found.
 */
var SEAL_RENDERERS = {
    BallotBoxSealed: function (t, _details, message) {
        var values = bodyValues(message, "BallotBoxSealed");
        if (!values)
            return null;
        var hash = values[2], inBox = values[3], counted = values[4], request = values[5];
        var notCounted = count(inBox) - count(counted);
        return __spreadArray(__spreadArray([
            String(t("logsScreen.ballotBoxSeal.sealHash", { hash: String(hash !== null && hash !== void 0 ? hash : "") })),
            String(t("logsScreen.ballotBoxSeal.counted", {
                counted: count(counted),
                inBox: count(inBox),
            }))
        ], (notCounted > 0
            ? [String(t("logsScreen.ballotBoxSeal.notCounted", { count: notCounted }))]
            : []), true), [
            request
                ? String(t("logsScreen.ballotBoxSeal.closeRequest", { request: String(request) }))
                : String(t("logsScreen.ballotBoxSeal.noCloseRequest")),
        ], false);
    },
    BallotBoxSealFailed: function (t, _details, message) {
        var _a;
        var values = bodyValues(message, "BallotBoxSealFailed");
        if (!values)
            return null;
        return [
            String(t("logsScreen.ballotBoxSeal.failedReason", { reason: String((_a = values[2]) !== null && _a !== void 0 ? _a : "") })),
            String(t("logsScreen.ballotBoxSeal.failedLocked")),
        ];
    },
    TallyBallotBoxVerified: function (t, _details, message) {
        var values = bodyValues(message, "TallyBallotBoxVerified");
        if (!values)
            return null;
        var hash = values[2], counted = values[3], session = values[4];
        return [
            String(t("logsScreen.ballotBoxSeal.sealHash", { hash: String(hash !== null && hash !== void 0 ? hash : "") })),
            String(t("logsScreen.ballotBoxSeal.verifiedCounted", { counted: count(counted) })),
            String(t("logsScreen.ballotBoxSeal.tallySession", { session: String(session !== null && session !== void 0 ? session : "") })),
        ];
    },
    TallyBallotBoxRejected: function (t, _details, message) {
        var values = bodyValues(message, "TallyBallotBoxRejected");
        if (!values)
            return null;
        var differs = values[2], session = values[3];
        return [
            String(t("logsScreen.ballotBoxSeal.differs", { differs: String(differs !== null && differs !== void 0 ? differs : "") })),
            String(t("logsScreen.ballotBoxSeal.tallySession", { session: String(session !== null && session !== void 0 ? session : "") })),
        ];
    },
};
var TENANT_ACTIVITY_OUTCOMES = ["succeeded", "denied", "failed"];
/** A detail's value as text: strings as they are, anything else as JSON. */
var detailText = function (value) {
    return typeof value === "string" ? value : JSON.stringify(value);
};
/**
 * Readable lines for the entries of a tenant's log: what was asked for, how
 * it ended, who asked and what it was asked on.
 */
var TENANT_RENDERERS = {
    TenantAdminAction: function (t, details) {
        var _a, _b, _c, _d, _e;
        if (!details)
            return null;
        var outcome = String((_a = details.outcome) !== null && _a !== void 0 ? _a : "");
        var target = details.details;
        return __spreadArray(__spreadArray(__spreadArray([
            String(t("tenantLogs.explanation.action", { action: String((_b = details.action) !== null && _b !== void 0 ? _b : "") }))
        ], (TENANT_ACTIVITY_OUTCOMES.includes(outcome)
            ? [
                String(t("tenantLogs.explanation.outcome.".concat(outcome), {
                    status: String((_c = details.status) !== null && _c !== void 0 ? _c : ""),
                })),
            ]
            : []), true), (details.username || details.user_id
            ? [
                String(t("tenantLogs.explanation.by", {
                    username: String((_d = details.username) !== null && _d !== void 0 ? _d : "-"),
                    userId: String((_e = details.user_id) !== null && _e !== void 0 ? _e : "-"),
                })),
            ]
            : []), true), (target && typeof target === "object" && !Array.isArray(target)
            ? Object.entries(target)
                .filter(function (_a) {
                var value = _a[1];
                return value !== null && value !== undefined;
            })
                .map(function (_a) {
                var name = _a[0], value = _a[1];
                return String(t("tenantLogs.explanation.detail", {
                    name: name,
                    value: detailText(value),
                }));
            })
            : []), true);
    },
    TenantLogOpened: function (t, details) {
        var _a, _b;
        if (!details)
            return null;
        return [
            String(t("tenantLogs.explanation.board", { board: String((_a = details.board) !== null && _a !== void 0 ? _a : "") })),
            String(t("tenantLogs.explanation.publicKey", { key: String((_b = details.public_key) !== null && _b !== void 0 ? _b : "") })),
        ];
    },
};
/**
 * Readable lines for the entries whose details explain a scheduled outcome,
 * keyed by statement kind. Other kinds show only their description.
 */
var RENDERERS = __assign(__assign(__assign(__assign({ ReadinessVerified: function (t, _details, message) { return readinessCheckLines(t, message); } }, TENANT_RENDERERS), { ScheduledOutcomeChanged: function (t, details) {
        var change = outcomeChange(details);
        if (!change)
            return null;
        return __spreadArray([
            String(t("logsScreen.scheduledOutcome.changed", {
                after: outcomeText(t, change.after),
                before: outcomeText(t, change.before),
            }))
        ], reasons(t, change.after), true);
    } }), SEAL_RENDERERS), { SigningActionExecuted: function (t, details) {
        var explanation = outcomeExplanation(details);
        var reason = sealReason(t, details);
        if (!explanation && !reason)
            return null;
        return __spreadArray(__spreadArray([], (explanation
            ? __spreadArray([
                String(t("logsScreen.scheduledOutcome.result", {
                    outcome: outcomeText(t, explanation),
                }))
            ], reasons(t, explanation), true) : []), true), (reason ? [reason] : []), true);
    } });
/** The explanation lines of a log row, or null when its kind has none. */
export var explanationLines = function (t, kind, message) {
    var renderer = kind ? RENDERERS[kind] : undefined;
    return renderer ? renderer(t, logDetails(message), message) : null;
};
export var StatementExplanation = function (_a) {
    var kind = _a.kind, message = _a.message;
    var t = useTranslation().t;
    var lines = explanationLines(t, kind, message);
    if (!(lines === null || lines === void 0 ? void 0 : lines.length))
        return null;
    return (_jsx(Box, { component: "ul", sx: { m: 0, mt: 0.5, pl: 2 }, className: "log-explanation", children: lines.map(function (line) { return (_jsx(Typography, { component: "li", variant: "body2", sx: { overflowWrap: "anywhere" }, children: line }, line)); }) }));
};
